<div class="nryf-event-details-page">
  @if (loading) {
    <div class="container text-center py-5" style="max-width: 1400px;">
      <div class="loading-spinner">
        <i class="fa fa-spinner fa-spin fa-3x text-info"></i>
        <p class="mt-3 text-muted">{{ 'common.loading' | translate }}</p>
      </div>
    </div>
  } @else if (!event) {
    <div class="container text-center py-5" style="max-width: 1400px;">
      <div class="not-found">
        <i class="fa fa-calendar-times fa-4x text-muted mb-3"></i>
        <p class="text-muted">{{ 'nryfEvent.notFound' | translate }}</p>
        <button class="btn btn-info mt-3" (click)="goBack()">
          {{ 'nryfEvent.backToEvents' | translate }}
        </button>
      </div>
    </div>
  } @else {
    <div class="container" style="max-width: 1400px;">
      <!-- Back Button -->
      <div class="back-button-wrapper">
        <button class="back-button" (click)="goBack()">
          <i class="fa fa-arrow-left me-2"></i>
          {{ 'nryfEvent.backToEvents' | translate }}
        </button>
      </div>

      <!-- Header Banner -->
      <div class="event-header">
        <div class="event-header-content">
          <!-- Badges -->
          <div class="header-badges">
              <span class="nryf-badge">NRYF</span>
              @if (getDisciplineName()) {
                <span class="discipline-badge">{{ getDisciplineName() }}</span>
              }
              @for (level of event.competitionLevels; track level.id) {
                <span class="level-badge">{{ level.name }}</span>
              }
            </div>

            <!-- Event Title -->
            <h1 class="event-title">{{ event.name }}</h1>

            <!-- Event Meta -->
            <div class="event-meta">
              @if (event.organiser) {
                <span class="event-meta-item">
                  <i class="fa fa-building me-2"></i>
                  {{ event.organiser.fullName || event.organiser.name }}
                </span>
              }
              <span class="event-meta-item">
                <i class="fa fa-calendar me-2"></i>
                {{ formatDateRange() }}
              </span>
              @if (event.place || event.venueAddress) {
                <span class="event-meta-item">
                  <i class="fa fa-location-dot me-2"></i>
                  {{ event.place || event.venueAddress }}
                </span>
              }
            </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="content-wrapper">
        <div class="row">
          <!-- Main Content -->
          <div class="col-lg-8">
            <!-- Registration Card (Mobile) -->
            <div class="d-lg-none mb-4">
              <div class="registration-card">
                <div class="registration-card-body">
                  <div class="registration-stats">
                    @if (event.registrationEndDate) {
                      <div class="stat-item">
                        <span class="stat-label">{{ 'nryfEvent.deadline' | translate }}</span>
                        <span class="stat-value">{{ formatDate(event.registrationEndDate) }}</span>
                      </div>
                    }
                    @if (event.competitions?.length) {
                      <div class="stat-item">
                        <span class="stat-label">{{ 'nryfEvent.classes' | translate }}</span>
                        <span class="stat-value">{{ event.competitions?.length }}</span>
                      </div>
                    }
                  </div>
                  <a [href]="registrationUrl" target="_blank" rel="noopener noreferrer" class="btn-register">
                    {{ 'nryfEvent.registerOnNryf' | translate }}
                    <i class="fa fa-external-link-alt ms-2"></i>
                  </a>
                  <p class="nryf-notice">
                    <i class="fa fa-info-circle me-1"></i>
                    {{ 'nryfEvent.registrationNotice' | translate }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Classes/Competitions - Most important for users -->
            @if (event.competitions?.length) {
              <section class="content-section">
                <h3>
                  <i class="fa fa-trophy section-icon"></i>
                  {{ 'nryfEvent.classes' | translate }}
                  <span class="count-badge">{{ event.competitions?.length }}</span>
                </h3>

                @for (group of getCompetitionsByDate(); track group.date) {
                  <div class="competition-date-group">
                    <div class="date-header">
                      <i class="fa fa-calendar-day me-2"></i>
                      {{ formatCompetitionDate(group.date) }}
                    </div>
                    <div class="classes-list">
                      @for (comp of group.competitions; track comp.id) {
                        <div class="class-item">
                          <div class="class-code">{{ comp.code }}</div>
                          <div class="class-info">
                            @if (comp.name) {
                              <span class="class-name">{{ comp.name }}</span>
                            }
                            @if (comp.description) {
                              <span class="class-description">{{ comp.description }}</span>
                            }
                          </div>
                          <div class="class-details">
                            @if (comp.registrationFeeTotalCents) {
                              <span class="class-fee">
                                {{ formatPrice(comp.registrationFeeTotalCents) }}
                              </span>
                            }
                            @if (comp.registrationsCount) {
                              <span class="class-registrations">
                                <i class="fa fa-users me-1"></i>
                                {{ comp.registrationsCount }}
                              </span>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                }
              </section>
            }

            <!-- Description -->
            @if (event.description) {
              <section class="content-section">
                <h3>
                  <i class="fa fa-info-circle section-icon"></i>
                  {{ 'nryfEvent.about' | translate }}
                </h3>
                <div class="description-content" [innerHTML]="event.description"></div>
              </section>
            }

            <!-- Info Fields - Sorted by priority -->
            @if (event.infoFields?.length) {
              @for (field of getSortedInfoFields(); track field.id) {
                <section class="content-section">
                  <h3>
                    <i class="fa fa-file-alt section-icon"></i>
                    {{ field.categoryName }}
                  </h3>
                  <div class="info-content" [innerHTML]="field.text"></div>
                </section>
              }
            }
          </div>

          <!-- Sidebar -->
          <div class="col-lg-4">
            <div class="sidebar-sticky-wrapper">
              <!-- Registration Card (Desktop) -->
              <div class="d-none d-lg-block">
                <div class="registration-card mb-4">
                  <div class="registration-card-header">
                    <i class="fa fa-clipboard-list me-2"></i>
                    {{ 'nryfEvent.registration' | translate }}
                  </div>
                  <div class="registration-card-body">
                    @if (event.registrationEndDate) {
                      <div class="deadline-info">
                        <span class="label">{{ 'nryfEvent.deadline' | translate }}</span>
                        <span class="value">{{ formatDate(event.registrationEndDate) }}</span>
                      </div>
                    }
                    @if (event.competitions?.length) {
                      <div class="classes-count">
                        <span class="label">{{ 'nryfEvent.classes' | translate }}</span>
                        <span class="value">{{ event.competitions?.length }}</span>
                      </div>
                    }
                    <a [href]="registrationUrl" target="_blank" rel="noopener noreferrer" class="btn-register">
                      {{ 'nryfEvent.registerOnNryf' | translate }}
                      <i class="fa fa-external-link-alt ms-2"></i>
                    </a>
                    <p class="nryf-notice">
                      <i class="fa fa-info-circle me-1"></i>
                      {{ 'nryfEvent.registrationNotice' | translate }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Location Card - Important for users to know where -->
              @if (mapMarkers.length > 0) {
                <div class="sidebar-card">
                  <h4>
                    <i class="fa fa-map-marked-alt card-icon"></i>
                    {{ 'nryfEvent.location' | translate }}
                  </h4>
                  <div class="map-container">
                    <app-location-map
                      [markers]="mapMarkers"
                      [config]="mapConfig"
                    ></app-location-map>
                  </div>
                  @if (event.venueAddress) {
                    <div class="venue-address">
                      <i class="fa fa-map-marker-alt me-2"></i>
                      <span>{{ event.venueAddress }}</span>
                    </div>
                    <a [href]="getGoogleMapsUrl()" target="_blank" class="btn-directions">
                      <i class="fa fa-directions me-2"></i>
                      {{ 'nryfEvent.getDirections' | translate }}
                    </a>
                  }
                </div>
              }

              <!-- Organizer Card -->
              @if (event.organiser) {
                <div class="sidebar-card">
                  <h4>
                    <i class="fa fa-building card-icon"></i>
                    {{ 'nryfEvent.organizer' | translate }}
                  </h4>
                  <div class="organizer-info">
                    @if (event.organiser.logoUrl || event.organiser.logoPath) {
                      <img
                        [src]="event.organiser.logoUrl || event.organiser.logoPath"
                        [alt]="event.organiser.name"
                        class="organizer-logo"
                      />
                    }
                    <div class="organizer-details">
                      <strong>{{ event.organiser.fullName || event.organiser.name }}</strong>
                      @if (event.organiser.address?.addressCity) {
                        <p class="text-muted mb-0">
                          <i class="fa fa-map-marker-alt me-1"></i>
                          {{ event.organiser.address?.addressCity }}
                        </p>
                      }
                    </div>
                  </div>
                </div>
              }

              <!-- Contact Card -->
              @if (event.contactPersonName || event.contactEmail || event.contactPhone) {
                <div class="sidebar-card">
                  <h4>
                    <i class="fa fa-address-card card-icon"></i>
                    {{ 'nryfEvent.contact' | translate }}
                  </h4>
                  <div class="contact-info">
                    @if (event.contactPersonName) {
                      <p class="contact-name">
                        <i class="fa fa-user me-2"></i>
                        {{ event.contactPersonName }}
                      </p>
                    }
                    @if (event.contactEmail) {
                      <p class="contact-email">
                        <i class="fa fa-envelope me-2"></i>
                        <a [href]="'mailto:' + event.contactEmail">{{ event.contactEmail }}</a>
                      </p>
                    }
                    @if (event.contactPhone) {
                      <p class="contact-phone mb-0">
                        <i class="fa fa-phone me-2"></i>
                        <a [href]="'tel:' + event.contactPhone">{{ event.contactPhone }}</a>
                      </p>
                    }
                  </div>
                </div>
              }

              <!-- Region Info -->
              @if (getRegionName()) {
                <div class="sidebar-card">
                  <h4>
                    <i class="fa fa-map card-icon"></i>
                    {{ 'nryfEvent.region' | translate }}
                  </h4>
                  <p class="mb-0">{{ getRegionName() }}</p>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  }
</div>
